import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Tag } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeUp, CTABand, EASE } from "@/components/Reveal";
import { ARTICLES } from "@/data/articles";

export default function Kennisbank() {
  return (
    <>
      <SEO
        title="Kennisbank | Alles over het energielabel | Directelabels"
        description="Praktische artikelen over het energielabel: wanneer is het verplicht, wat kost het en hoe verbetert u het label van uw woning? Advies van Directelabels, actief in Rotterdam en Zuid-Holland."
        path="/kennisbank"
      />

      <section data-testid="kennisbank-hero" className="mx-auto max-w-7xl px-5 pb-8 pt-14 md:px-8 md:pt-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-xs font-bold uppercase tracking-[0.24em] text-primary"
        >
          Kennisbank
        </motion.p>
        <h1 data-testid="kennisbank-title" className="mt-6 font-display text-4xl font-semibold leading-tight tracking-tighter text-ink md:text-6xl">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>
              Alles over het <span className="text-primary">energielabel.</span>
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
        >
          Duidelijke artikelen over verplichtingen, kosten en verduurzaming — geschreven voor woningeigenaren,
          verhuurders en makelaars.
        </motion.p>
      </section>

      <section data-testid="kennisbank-grid" className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <div className="grid gap-6 md:grid-cols-3">
          {ARTICLES.map((a, i) => (
            <FadeUp key={a.slug} delay={i * 0.1}>
              <Link
                to={`/kennisbank/${a.slug}`}
                data-testid={`artikel-card-${a.slug}`}
                className="group flex h-full flex-col rounded-3xl border border-black/5 bg-white p-8 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
              >
                <div className="flex items-center gap-3 text-xs font-bold text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-energy-pale px-3 py-1 text-energy-deep">
                    <Tag size={11} />
                    {a.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={12} />
                    {a.readTime}
                  </span>
                </div>
                <h2 className="mt-5 font-display text-xl font-medium leading-snug tracking-tight text-ink transition-colors duration-200 group-hover:text-primary md:text-2xl">
                  {a.title}
                </h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{a.description}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary">
                  Lees het artikel
                  <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      <CTABand />
    </>
  );
}
